import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { useAuthHeader } from '@/hooks/useAuth';
import { getProductGroupLevelCounts, getProductGroupTypes } from '@/lib/api/product-group-type';
import { getWarehouseTree } from '@/lib/api/warehouse';
import {
  getLatestWeeklyStoreProductGroupDefaults,
  startWeeklyStoreProductGroupExtraction,
} from '@/lib/api/analysis-data-extraction';
import type { ProductGroupLevelCounts, ProductGroupType, WarehouseTreeNode } from '@/lib/types';
import type {
  AggregationMode,
  ExportPeriodType,
  ProductGroupSelection,
  StoreAggregationMode,
  WeeklyStoreProductGroupDefaults,
  WeekStartDay,
} from '@/lib/types/feature/analysis-data-extraction';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SegmentedToggle, type SegmentedToggleOption } from '@/components/ui/segmented-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WarehouseQuickSelect } from './WarehouseQuickSelect';
import { WarehouseMultiSelect } from './WarehouseMultiSelect';
import { expandGroupsToWarehouseIds, topLevelGroupIds } from '@/lib/utils/warehouse-tree';
import {
  monthBucketEnd,
  monthBucketStart,
  monthKeyOfDate,
  monthLabel,
  type MonthKey,
} from '@/lib/utils/month-bucket';
import { StoreGranularitySelect } from './StoreGranularitySelect';
import { StoreGroupExclusiveSelect } from './StoreGroupExclusiveSelect';

/** タイプ別（縦持ち）で選択できる商品グループタイプの上限（倉庫出荷効果検証に合わせる） */
const MAX_SELECTED_TYPES = 15;
/** 掛け合わせ（横持ち）で選択できる商品グループタイプの上限。バックエンドのSQLが3スロット固定 */
const MAX_CROSS_TYPES = 3;
/**
 * 掛け合わせで選択したタイプの集計階層の既定値。
 *
 * 縦持ちは最下層（null）が既定だが、掛け合わせは行数が各タイプのグループ数の積で増えるため
 * 1階層から始める。abdi の実測では、2244グループのタイプを含む3タイプの掛け合わせで
 * 最下層 → 1階層に変えると結果CSVが 1.79GB → 87MB（20分の1）、所要時間が 4.4倍速になった。
 * 深く見たい場合は集計階層の調整パネルで明示的に変更する。
 */
const CROSS_DEFAULT_DEPTH = 1;
/**
 * 掛け合わせの組み合わせ数がこれを超えたら警告する。
 *
 * abdi の実測（3タイプ×5週）では、約21,800通りで結果CSVが 1.79GB（Excelで開けない規模）、
 * 約160通りで 87MB だった。10,000通りは「Excelの上限100万行に週×店舗を掛けても収まらなくなる」
 * 目安として置いている。
 */
const CROSS_COMBINATION_WARN_THRESHOLD = 10_000;

/** 集計単位カードの定義。出力列イメージは選択前から両方見えるようにする */
const AGGREGATION_MODE_OPTIONS: {
  value: AggregationMode;
  id: string;
  title: string;
  description: string;
}[] = [
  {
    value: 'by_type',
    id: 'aggregation-mode-by-type',
    title: 'タイプ別（縦持ち）',
    description: `最大${MAX_SELECTED_TYPES}タイプを縦に積んで出力`,
  },
  {
    value: 'cross',
    id: 'aggregation-mode-cross',
    title: '掛け合わせ（横持ち）',
    description: `最大${MAX_CROSS_TYPES}タイプを列に並べて出力`,
  },
];

/**
 * 出力列イメージの1マス。
 * placeholder は「実データではなく例」であることが分かるよう破線＋淡色にする。
 */
function PreviewColumn({ label, placeholder }: { label: string; placeholder?: boolean }) {
  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-[11px] leading-4',
        placeholder
          ? 'border-dashed border-gray-300 text-gray-400'
          : 'border-gray-300 bg-gray-50 text-gray-700',
      )}
    >
      {label}
    </span>
  );
}
/** 商品グループ集計階層の選択肢。null は「最下層」（既定） */
const DEPTH_OPTIONS: (number | null)[] = [null, 1, 2, 3, 4, 5];
/** 集計階層の表示ラベル */
const depthLabel = (depth: number | null) => (depth === null ? '最下層' : `${depth}階層`);
/** 集計粒度（週次 / 月次）の選択肢（FKV3-13085） */
const PERIOD_TYPE_OPTIONS: readonly [
  SegmentedToggleOption<ExportPeriodType>,
  SegmentedToggleOption<ExportPeriodType>,
] = [
  { value: 'weekly', label: '週次' },
  { value: 'monthly', label: '月次' },
];

/** 非稼働判定日数の既定値・範囲 */
const DEFAULT_INACTIVE_DAYS = 30;
const INACTIVE_DAYS_MIN = 1;
const INACTIVE_DAYS_MAX = 365;

interface WeeklyStoreProductGroupFormProps {
  account: { id: string; code: string; name: string };
  /** 実行受付に成功したときのコールバック（実行ログタブへの切替に使う） */
  onSubmitted?: () => void;
}

/**
 * [週次/月次] 店舗×商品グループ実績抽出の実行フォーム
 * 集計粒度（週次 / 月次）・集計期間・集計単位・商品グループタイプ・集計対象倉庫・非稼働判定日数を入力して実行する。
 * 週始まりは週次のときだけ指定する（月次はアカウント設定の月次締日で区切るため）。
 *
 * 集計単位は排他選択で、選んだ側だけタイプ選択の上限と出力列が変わる。
 * - タイプ別（縦持ち）: 最大15タイプ。出力は「商品Gタイプ名 / 商品G階層」の2列
 * - 掛け合わせ（横持ち）: 最大3タイプ。出力列名は選択したタイプ名そのもので、選んだ順が列順
 *
 * 集計階層の既定は、縦持ちが最下層、掛け合わせが1階層（行数が各タイプのグループ数の積で
 * 増えるため浅くして始める）。折りたたみ内の「集計階層を調整する」からタイプごとに変更でき、
 * 掛け合わせではこのパネルを既定で開く。
 * 掛け合わせでは組み合わせ数の上限を実行前に見積もって、大きすぎる場合は警告を出す。
 * 出力はZIP内の2つのCSV（店舗明細と、物流倉庫・発注残の在庫）。集計対象倉庫の選択は店舗明細CSVの
 * 範囲を決めるもので、物流倉庫・発注残CSVは選択に関わらず常に全量出力する（Redash #1627 と同じ）。
 */
export function WeeklyStoreProductGroupForm({
  account,
  onSubmitted,
}: WeeklyStoreProductGroupFormProps) {
  const { getAuthHeader } = useAuthHeader();

  // 入力state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // 集計粒度（週次 / 月次）。既定は従来どおり週次（FKV3-13085）
  const [periodType, setPeriodType] = useState<ExportPeriodType>('weekly');
  const [weekStartDay, setWeekStartDay] = useState<WeekStartDay>('monday');
  const isMonthly = periodType === 'monthly';
  // 月次集計の締日（アカウント設定）。null は暦月。前回実行条件の取得と同時に受け取る
  const [monthlyClosingDate, setMonthlyClosingDate] = useState<number | null>(null);
  const [aggregationMode, setAggregationMode] = useState<AggregationMode>('by_type');
  // 商品グループ選択。配列の順序は掛け合わせ時に出力列の順序を意味するため、順序を型で保持する。
  // 集計単位ごとに独立して持つ理由: 上限が違うため片方に寄せると切り捨てが必要になり、
  // 「掛け合わせを覗いただけでタイプ別の選択が消える」という破壊的な挙動になる。
  const [byTypeSelections, setByTypeSelections] = useState<ProductGroupSelection[]>([]);
  const [crossSelections, setCrossSelections] = useState<ProductGroupSelection[]>([]);

  // 現在の集計単位のビュー。以降のUI・バリデーション・送信はすべてこれを見る
  const isCross = aggregationMode === 'cross';
  const selections = isCross ? crossSelections : byTypeSelections;
  const setSelections = isCross ? setCrossSelections : setByTypeSelections;
  const maxSelectedTypes = isCross ? MAX_CROSS_TYPES : MAX_SELECTED_TYPES;
  // 集計階層の調整欄は既定で閉じておく（通常は最下層のままで良いため）
  const [depthPanelOpen, setDepthPanelOpen] = useState(false);
  const [inactiveDays, setInactiveDays] = useState<number>(DEFAULT_INACTIVE_DAYS);
  // 前々年比較ファイルも出力するか（既定 false。FKV3-12587）
  const [includeTwoYearsAgo, setIncludeTwoYearsAgo] = useState(false);
  // 店舗軸の出力粒度。店舗グループ別を選ぶと店舗明細が店舗グループ単位に丸まる（FKV3-12856）
  const [storeAggregationMode, setStoreAggregationMode] =
    useState<StoreAggregationMode>('by_seller');
  // 集計する店舗グループ（選んだグループがそのまま出力の1行になる。FKV3-12858）
  const [storeGroupNodeIds, setStoreGroupNodeIds] = useState<Set<string>>(new Set());
  const [groupSelectionInitialized, setGroupSelectionInitialized] = useState(false);
  const isByGroup = storeAggregationMode === 'by_group';

  // 集計対象倉庫（デフォルトは全倉庫チェック）
  const [selectedTargetWarehouseIds, setSelectedTargetWarehouseIds] = useState<Set<string>>(
    new Set(),
  );
  const [targetInitialized, setTargetInitialized] = useState(false);
  // 前回実行条件（ログインユーザー単位）。undefined=取得前、null=前回実行なし
  const [pendingDefaults, setPendingDefaults] = useState<
    WeeklyStoreProductGroupDefaults | null | undefined
  >(undefined);
  // pendingDefaults の適用（フォームへの反映）を1回のみ行うためのフラグ
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // マスタ
  const [productGroupTypes, setProductGroupTypes] = useState<ProductGroupType[]>([]);
  // 集計階層別のグループ数（掛け合わせの組み合わせ数見積もり用）。掛け合わせを選んだときに遅延取得する
  const [levelCounts, setDepthCounts] = useState<ProductGroupLevelCounts[] | null>(null);
  const [levelCountsLoading, setDepthCountsLoading] = useState(false);
  // 取得済みフラグ。state を effect の依存に入れると、自分が起こした更新で cleanup が走り
  // 自分自身の取得をキャンセルしてしまうため ref で持つ
  const levelCountsRequested = useRef(false);
  // 倉庫ツリー（店舗グループ階層→倉庫。プロダクトの倉庫ツリーと同じ並び順）
  const [warehouseNodes, setWarehouseNodes] = useState<WarehouseTreeNode[]>([]);
  // ツリー中の倉庫（末端ノード）のIDリスト。全選択の初期化・全選択判定に使う
  const warehouseIds = useMemo(
    () => warehouseNodes.filter((n) => n.node_type === 'warehouse').map((n) => n.id),
    [warehouseNodes],
  );
  // 実際に送信する集計対象倉庫。前回実行条件から復元した倉庫が削除されている場合があるため、
  // 現存する倉庫だけに絞る。検証も送信もこの値だけを見る
  // （生の選択集合を見ると、復元した倉庫が全て削除済みのときに「選択あり」と誤判定し、
  //  絞ったつもりが空配列＝全倉庫で送信されてしまう）
  // 店舗グループ別では選んだグループの配下が出力範囲になる（範囲と集計単位を1つのツリーで選ぶ）
  const effectiveTargetWarehouseIds = useMemo(() => {
    if (isByGroup) {
      const expanded = expandGroupsToWarehouseIds(warehouseNodes, storeGroupNodeIds);
      return warehouseIds.filter((id) => expanded.has(id));
    }
    return warehouseIds.filter((id) => selectedTargetWarehouseIds.has(id));
  }, [isByGroup, warehouseNodes, warehouseIds, storeGroupNodeIds, selectedTargetWarehouseIds]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // マスタ（商品グループタイプ・倉庫ツリー）取得中フラグ。Athena経由で数秒かかるため「がありません」との誤表示を防ぐ。
  // 初回マウント時点で account.code が既に分かっていれば、下の取得 effect が直後に走るため
  // 最初から true にする。アカウント切替時は下のレンダー中の調整で true に戻す
  // （切替時の分岐が無いと、リセットが「変化を検知したときだけ」効くパターンのため
  //  初回マウントでは何もセットされず、フェッチ中に「がありません」の空状態が出てしまう）。
  const [mastersLoading, setMastersLoading] = useState(() => Boolean(account.code));

  // アカウント切替（リマウントなし）時に前アカウントの選択UUID・エラー表示を持ち越さないよう、
  // 状態をリセットする。これは effect ではなく「レンダー中の state 調整」パターンで行う
  // （https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes）。
  //
  // 以前は useEffect(..., [account.code]) でリセットしていたが、その場合
  // 「新しい account.code だが aggregationMode 等はまだ古い値」という状態でレンダーが
  // 一度コミットされ、その1回だけ他の effect（掛け合わせの組み合わせ数取得など）が
  // 古い state と新しい account.code の組み合わせで発火してしまう瞬間が生まれる
  // （実際に、掛け合わせ選択中にアカウントを切り替えると、新アカウント宛の取得が
  //  意図せず一度走ってからキャンセルされ、それが失敗すると新アカウントでは二度と
  //  組み合わせ数が出せなくなる不具合が起きた）。レンダー中に調整すればその中間状態自体が
  // 存在しなくなり、以降の全ての effect は最初から一貫した state で評価される。
  const prevAccountCodeRef = useRef(account.code);
  if (account.code !== prevAccountCodeRef.current) {
    prevAccountCodeRef.current = account.code;
    setStartDate('');
    setEndDate('');
    setPeriodType('weekly');
    setMonthlyClosingDate(null);
    setWeekStartDay('monday');
    setInactiveDays(DEFAULT_INACTIVE_DAYS);
    setIncludeTwoYearsAgo(false);
    setAggregationMode('by_type');
    setByTypeSelections([]);
    setCrossSelections([]);
    setDepthPanelOpen(false);
    setStoreAggregationMode('by_seller');
    setStoreGroupNodeIds(new Set());
    setGroupSelectionInitialized(false);
    setSelectedTargetWarehouseIds(new Set());
    setTargetInitialized(false);
    setPendingDefaults(undefined);
    setDefaultsApplied(false);
    setProductGroupTypes([]);
    setDepthCounts(null);
    levelCountsRequested.current = false;
    setWarehouseNodes([]);
    setError(null);
    setMastersLoading(true);
  }

  // 商品グループタイプ・倉庫マスタを取得
  // getAuthHeader はローカル環境でも即有効なヘッダを返すため、認証ロード状態は待たない
  // （既存の LogisticsShippingKpiV2 と同じパターン）。
  // アカウント識別子は全API（マスタ取得・ジョブ実行）で account.code に統一する。
  // バックエンドは account_code でテナント（Athena schema 等）を解決するため、id と code が
  // 異なるテナントでも取得先とジョブ実行先が一致する。
  // 状態のリセット自体は上のレンダー中の調整で完了しているため、ここでは取得のみを行う。
  useEffect(() => {
    if (!account.code) return;
    // アカウント切替後に前アカウントの遅延レスポンスがリセット済みの状態を上書きしないようにする
    let cancelled = false;
    const authHeaders = getAuthHeader();
    const typesPromise = getProductGroupTypes(account.code, authHeaders)
      .then((types) => {
        if (!cancelled) setProductGroupTypes(types);
      })
      .catch(() => {
        if (!cancelled) setError('商品グループタイプの取得に失敗しました');
      });
    const treePromise = getWarehouseTree(account.code, authHeaders)
      .then((nodes) => {
        if (!cancelled) setWarehouseNodes(nodes);
      })
      .catch(() => {
        if (!cancelled) setError('倉庫一覧の取得に失敗しました');
      });
    // 前回実行条件は取得できなくても入力自体は継続できるため、失敗時はエラー表示せず「前回実行なし」として扱う
    const defaultsPromise = getLatestWeeklyStoreProductGroupDefaults(account.code, authHeaders)
      .then((res) => {
        if (cancelled) return;
        setPendingDefaults(res.has_previous_execution ? res.parameters : null);
        // 締日は前回実行条件とは別のアカウント設定。同じレスポンスに相乗りしている
        setMonthlyClosingDate(res.monthly_closing_date ?? null);
      })
      .catch(() => {
        if (!cancelled) setPendingDefaults(null);
      });
    Promise.allSettled([typesPromise, treePromise, defaultsPromise]).then(() => {
      if (!cancelled) setMastersLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 集計階層別のグループ数は掛け合わせのときだけ必要なので、初めて選ばれたときに取得する
  // （組み合わせ数の見積もりに使うだけなので、取得できなくても抽出自体は実行できる）。
  //
  // マスタ取得と同じく cancelled ガードを付ける。無いと、アカウント切替で前アカウントの
  // 遅いレスポンスが後から届き、新アカウントの見積もり（levelCounts）を上書きしてしまう
  // （アカウント切替自体は上のレンダー中の調整で行うため、切替時に古い state と新しい
  //  account.code が混在した状態でこの effect が誤発火することはない）。
  //
  // 完了前にキャンセルされた場合は levelCountsRequested を「未取得」に戻す（settled で判定）。
  // 同一アカウント内でユーザーが取得中に「掛け合わせ」を一度外して選び直した場合など、
  // isCross が false→true と変化してこの effect が再評価されるケースで、キャンセルされた
  // 取得を「取得済み」のまま固定してしまうと、選び直しても二度と取得されなくなる。
  useEffect(() => {
    if (!isCross || !account.code || levelCounts !== null || levelCountsRequested.current) return;
    levelCountsRequested.current = true;
    let cancelled = false;
    let settled = false;
    setDepthCountsLoading(true);
    getProductGroupLevelCounts(account.code, getAuthHeader())
      .then((counts) => {
        settled = true;
        if (!cancelled) setDepthCounts(counts);
      })
      // 見積もりが出せないだけなので、空配列を入れて「取得済みだが該当なし」にする
      .catch(() => {
        settled = true;
        if (!cancelled) setDepthCounts([]);
      })
      .finally(() => {
        if (!cancelled) setDepthCountsLoading(false);
      });
    return () => {
      cancelled = true;
      if (!settled) {
        levelCountsRequested.current = false;
        setDepthCountsLoading(false);
      }
    };
  }, [isCross, account.code, levelCounts]);

  // 倉庫取得後、集計対象倉庫を初回のみ全チェックにする
  // （前回実行条件の対象倉庫が「全倉庫」＝空配列だった場合もこの効果に委ねる）
  useEffect(() => {
    if (!targetInitialized && warehouseIds.length > 0) {
      setSelectedTargetWarehouseIds(new Set(warehouseIds));
      setTargetInitialized(true);
    }
  }, [warehouseIds, targetInitialized]);

  // 店舗グループ別を初めて選んだときは最上位グループを既定にする（全社1行から始める）。
  // 一度初期化したら、利用者が全解除した状態を勝手に埋め直さない
  useEffect(() => {
    if (!isByGroup || groupSelectionInitialized || warehouseNodes.length === 0) return;
    setStoreGroupNodeIds(topLevelGroupIds(warehouseNodes));
    setGroupSelectionInitialized(true);
  }, [isByGroup, groupSelectionInitialized, warehouseNodes]);

  // 前回実行条件をフォームへ反映（アカウント切替ごとに1回のみ）
  useEffect(() => {
    if (defaultsApplied || pendingDefaults === undefined) return;
    if (pendingDefaults) {
      setStartDate(pendingDefaults.start_date);
      setEndDate(pendingDefaults.end_date);
      // FKV3-13085 より前のジョブは集計粒度を持たないため、週次（現行動作）に落とす
      setPeriodType(pendingDefaults.period_type ?? 'weekly');
      setWeekStartDay(pendingDefaults.week_start_day);
      // FKV3-12856 より前のジョブには店舗軸の集計単位が無いため、店舗別（現行動作）に落とす
      setStoreAggregationMode(pendingDefaults.store_aggregation_mode ?? 'by_seller');
      // FKV3-12858 より前のジョブは選択を持たないため、既定（最上位グループ）に委ねる
      if (pendingDefaults.store_group_node_ids?.length) {
        setStoreGroupNodeIds(new Set(pendingDefaults.store_group_node_ids));
        setGroupSelectionInitialized(true);
      }
      // FKV3-12586 より前のジョブには集計単位が無いため、タイプ別（現行動作）にフォールバックする
      const restoredMode: AggregationMode = pendingDefaults.aggregation_mode ?? 'by_type';
      setAggregationMode(restoredMode);
      setDepthPanelOpen(restoredMode === 'cross');
      // 旧形式(depth 必須)・新形式(depth 省略) いずれも depth ?? null で最下層に寄せる
      const restored = pendingDefaults.product_group_selections.map((s) => ({
        type_id: s.type_id,
        depth: s.depth ?? null,
      }));
      // 上限を超えたデータでもUIの不変条件を壊さないよう切り詰める
      if (restoredMode === 'cross') {
        setCrossSelections(restored.slice(0, MAX_CROSS_TYPES));
      } else {
        setByTypeSelections(restored.slice(0, MAX_SELECTED_TYPES));
      }
      setInactiveDays(pendingDefaults.inactive_days);
      // FKV3-12587 より前のジョブには無いため、既定 false にフォールバックする
      setIncludeTwoYearsAgo(pendingDefaults.include_two_years_ago ?? false);
      // 空配列＝前回「全倉庫」選択のため、上の「倉庫取得後に全チェック」効果に委ねる
      if (pendingDefaults.target_warehouse_ids.length > 0) {
        setSelectedTargetWarehouseIds(new Set(pendingDefaults.target_warehouse_ids));
        setTargetInitialized(true);
      }
    }
    setDefaultsApplied(true);
  }, [pendingDefaults, defaultsApplied]);

  // 選択済みタイプの位置（0始まり）。選択判定と、掛け合わせ時の列順表示に使う
  const selectedIndexById = useMemo(
    () => new Map(selections.map((s, index) => [s.type_id, index])),
    [selections],
  );

  // 商品グループタイプの選択トグル（末尾に追加。既定の集計階層は集計単位で異なる。上限あり）
  const toggleType = (typeId: string) => {
    setSelections((prev) => {
      if (prev.some((s) => s.type_id === typeId)) {
        return prev.filter((s) => s.type_id !== typeId);
      }
      if (prev.length >= maxSelectedTypes) return prev;
      return [...prev, { type_id: typeId, depth: isCross ? CROSS_DEFAULT_DEPTH : null }];
    });
  };

  // 選択済みタイプの集計階層を変更（null＝最下層）。並び順は変えない
  const changeDepth = (typeId: string, depth: number | null) => {
    setSelections((prev) => prev.map((s) => (s.type_id === typeId ? { ...s, depth } : s)));
  };

  // 掛け合わせの列順を入れ替える（上限3件なのでドラッグ&ドロップは不要）
  const moveCrossSelection = (index: number, direction: -1 | 1) => {
    setCrossSelections((prev) => {
      const to = index + direction;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const handleAggregationModeChange = (mode: AggregationMode) => {
    setAggregationMode(mode);
    // 掛け合わせを「初めて」開いたときだけタイプ別の先頭3件を引き継ぐ。
    // 空のときに限定しないと、掛け合わせ↔タイプ別を往復するたびに列順が上書きされて失われる。
    if (mode === 'cross') {
      setCrossSelections((prev) =>
        prev.length > 0
          ? prev
          : byTypeSelections
              .slice(0, MAX_CROSS_TYPES)
              // 引き継ぎ元（縦持ち）の既定は最下層だが、掛け合わせでは行数が積で増えるため浅くして始める
              .map((s) => ({ ...s, depth: s.depth ?? CROSS_DEFAULT_DEPTH })),
      );
    }
    // 掛け合わせは行数がグループ数の積で増えるため、階層の調整を最初から見せる
    setDepthPanelOpen(mode === 'cross');
  };

  // 掛け合わせの列順プレビュー用。選択中のタイプ名を選んだ順に並べる
  const selectedTypeNames = useMemo(
    () =>
      selections.map((s) => productGroupTypes.find((t) => t.id === s.type_id)?.name ?? s.type_id),
    [selections, productGroupTypes],
  );

  /**
   * 掛け合わせの組み合わせ数の見積もり（選択中の集計階層でのグループ数の積）。
   * グループ数が取得できていないタイプがあれば null（見積もり不能）。
   *
   * これはマスタ全体を母数にした上限で、実際の行数はSKUが属する組み合わせにしか現れないため
   * これより小さくなる。過大に見えても「階層を浅くする」という判断は変わらないため上限で示す。
   */
  const crossEstimate = useMemo(() => {
    if (!isCross || levelCounts === null || selections.length === 0) return null;
    const perType = selections.map((s) => {
      const counts = levelCounts.find((c) => c.type_id === s.type_id);
      if (!counts) return null;
      const name = productGroupTypes.find((t) => t.id === s.type_id)?.name ?? s.type_id;
      // depth が null（最下層）なら lowest_count、1〜5 なら depth_counts[depth - 1]
      const current =
        s.depth === null ? counts.lowest_count : (counts.depth_counts[s.depth - 1] ?? 0);
      return { name, depth: s.depth, current, lowest: counts.lowest_count, counts };
    });
    if (perType.some((p) => p === null)) return null;
    const types = perType as NonNullable<(typeof perType)[number]>[];
    const total = types.reduce((acc, t) => acc * Math.max(t.current, 1), 1);
    // 全タイプを1階層にしたときの組み合わせ数（警告時に「これだけ減る」と示すため）
    const atDepth1 = types.reduce((acc, t) => acc * Math.max(t.counts.depth_counts[0] ?? 0, 1), 1);
    return { total, atDepth1, types };
  }, [isCross, levelCounts, selections, productGroupTypes]);

  // カード内の出力列イメージ。掛け合わせは選択中のとき実際のタイプ名を反映する
  const previewColumnsOf = (
    mode: AggregationMode,
  ): { key: string; label: string; placeholder?: boolean }[] => {
    const base = [
      { key: 'week', label: '週' },
      { key: 'store', label: '店舗' },
    ];
    if (mode === 'by_type') {
      return [
        ...base,
        { key: 'type-name', label: '商品Gタイプ名' },
        { key: 'type-depth', label: '商品G階層' },
        { key: 'metrics', label: '指標...' },
      ];
    }
    // 未選択時に「中分類」等の具体名を出すと、そのタイプが無いテナントで実データと誤認されるため
    // 汎用の商品G1〜G3をプレースホルダとして見せる
    const crossColumns =
      isCross && selectedTypeNames.length > 0
        ? selectedTypeNames.map((name, index) => ({ key: `cross-${index}`, label: name }))
        : [
            { key: 'g1', label: '商品G1', placeholder: true },
            { key: 'g2', label: '商品G2', placeholder: true },
            { key: 'g3', label: '商品G3', placeholder: true },
          ];
    return [...base, ...crossColumns, { key: 'metrics', label: '指標...' }];
  };

  // 月次のときに画面へ出す月。送信値は常に start/end の日付で、表示だけ月へ畳む
  const startMonth = startDate ? monthKeyOfDate(startDate, monthlyClosingDate) : ('' as MonthKey);
  const endMonth = endDate ? monthKeyOfDate(endDate, monthlyClosingDate) : ('' as MonthKey);

  /**
   * 集計粒度の切り替え。
   *
   * 月次へ移るときは選択中の日付をバケット境界へ寄せる。画面には月しか出さないため、
   * 寄せないと「表示は4月なのに送信は4/17から」というズレが見えないまま残る。
   */
  /** 月の選択を日付へ変換して反映する。月をクリアしたら日付も空にする */
  const applyMonthSelection = (
    rawMonth: string,
    toDate: (month: MonthKey, closingDate: number | null) => string,
    setDate: (date: string) => void,
  ) => {
    // `<input type="month">` はクリアできる。空のまま変換すると不正な日付になり、
    // 「期間を指定してください」ではなく期間上限のエラーが出てしまう
    setDate(rawMonth ? toDate(rawMonth as MonthKey, monthlyClosingDate) : '');
  };

  const handlePeriodTypeChange = (next: ExportPeriodType) => {
    setPeriodType(next);
    if (next !== 'monthly') return;
    if (startDate)
      setStartDate(
        monthBucketStart(monthKeyOfDate(startDate, monthlyClosingDate), monthlyClosingDate),
      );
    if (endDate)
      setEndDate(monthBucketEnd(monthKeyOfDate(endDate, monthlyClosingDate), monthlyClosingDate));
  };

  // 送信前バリデーション（詳細な期間上限はバックエンドが最終判定する）
  const validate = (): string | null => {
    if (!startDate || !endDate) return '集計期間（開始日・終了日）を指定してください';
    if (startDate > endDate) return '開始日は終了日以前を指定してください';

    // 期間上限: 開始日の2年後の応当日の前日。応当日が存在しない場合（例: 2/29開始）は
    // 月末に丸めた日を応当日とする（バックエンドの checked_add_months(24) と同一ロジック）
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const lastDayOfTargetMonth = new Date(Date.UTC(startYear + 2, startMonth, 0)).getUTCDate();
    const anniversary = new Date(
      Date.UTC(startYear + 2, startMonth - 1, Math.min(startDay, lastDayOfTargetMonth)),
    );
    anniversary.setUTCDate(anniversary.getUTCDate() - 1);
    const maxEndDate = anniversary.toISOString().slice(0, 10);
    if (endDate > maxEndDate) {
      return `集計期間は最長2年です（この開始日では終了日は${maxEndDate}まで）`;
    }

    if (selections.length === 0) {
      return isCross
        ? '掛け合わせる商品グループタイプを1つ以上選択してください'
        : '集計対象の商品グループタイプを1つ以上選択してください';
    }
    // UI上は選択チップの無効化で防いでいるが、集計単位別stateの不変条件が崩れたまま
    // 上限超えのジョブを投げるのを防ぐ最終防衛線として件数も検証する
    if (selections.length > maxSelectedTypes) {
      return `商品グループタイプは最大${maxSelectedTypes}件まで選択できます`;
    }
    // 全解除（0件）と全選択（＝全倉庫）を区別するため、0件は明示的に弾く
    if (isByGroup && storeGroupNodeIds.size === 0) {
      return '集計する店舗グループを1つ以上選択してください';
    }
    if (warehouseIds.length > 0 && effectiveTargetWarehouseIds.length === 0) {
      return isByGroup
        ? '選択した店舗グループに倉庫が紐づいていません'
        : '集計対象の倉庫を1つ以上選択してください';
    }
    if (inactiveDays < INACTIVE_DAYS_MIN || inactiveDays > INACTIVE_DAYS_MAX) {
      return `非稼働判定日数は${INACTIVE_DAYS_MIN}〜${INACTIVE_DAYS_MAX}の範囲で指定してください`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    // 集計対象倉庫が全選択なら空配列で送る（バックエンドで全倉庫扱い。IN句の肥大化を避ける）。
    // 全選択の判定に件数比較を使うため、倉庫の削除と追加が同数でも誤判定しないよう
    // 現存分に絞った effectiveTargetWarehouseIds で比較する
    const allTargetSelected =
      warehouseIds.length > 0 && effectiveTargetWarehouseIds.length === warehouseIds.length;
    const targetWarehouseIds = allTargetSelected ? [] : effectiveTargetWarehouseIds;

    try {
      setSubmitting(true);
      setError(null);
      const authHeaders = getAuthHeader();
      await startWeeklyStoreProductGroupExtraction(
        account.code,
        {
          start_date: startDate,
          end_date: endDate,
          period_type: periodType,
          // 月次ではバックエンドが無視するが、型を任意にしないため常に送る
          week_start_day: weekStartDay,
          // 配列の順序がそのまま意味を持つため加工せずに送る
          product_group_selections: selections,
          inactive_days: inactiveDays,
          target_warehouse_ids: targetWarehouseIds,
          aggregation_mode: aggregationMode,
          store_aggregation_mode: storeAggregationMode,
          // 店舗別のときは階層を送らない（バックエンドが「切り替えたつもりが効いていない」を弾く）
          store_group_node_ids: isByGroup ? Array.from(storeGroupNodeIds) : [],
          include_two_years_ago: includeTwoYearsAgo,
        },
        authHeaders,
      );
      toast.success('抽出ジョブを受け付けました。実行ログで進捗を確認できます');
      onSubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : '抽出ジョブの実行に失敗しました';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>抽出条件</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* 集計粒度（週次 / 月次。FKV3-13085） */}
        <div className="space-y-2">
          <Label id="period-type-label">集計粒度</Label>
          <div>
            <SegmentedToggle
              value={periodType}
              onValueChange={handlePeriodTypeChange}
              options={PERIOD_TYPE_OPTIONS}
              labelledBy="period-type-label"
              disabled={mastersLoading}
            />
          </div>
          <p className="text-sm text-gray-500">
            {isMonthly
              ? monthlyClosingDate === null
                ? '月の区切りは暦月（1日〜月末）です。前年は前年同月で比較します。'
                : `月の区切りはアカウント設定の月次締日（${monthlyClosingDate}日）に従います。前年は前年同月で比較します。`
              : '前年は364日前の同曜日週（同じ曜日並び）で比較します。'}
          </p>
        </div>

        {/* 集計期間。月次は月だけを選ばせ、実日付はラベルに併記する */}
        <div className="space-y-2">
          <Label>集計期間（最長2年）</Label>
          {isMonthly ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  type="month"
                  value={startMonth}
                  onChange={(e) =>
                    applyMonthSelection(e.target.value, monthBucketStart, setStartDate)
                  }
                  className="w-auto"
                  disabled={mastersLoading}
                />
                <span>〜</span>
                <Input
                  type="month"
                  value={endMonth}
                  onChange={(e) => applyMonthSelection(e.target.value, monthBucketEnd, setEndDate)}
                  className="w-auto"
                  disabled={mastersLoading}
                />
              </div>
              <p className="text-sm text-gray-500">
                {startMonth && endMonth
                  ? `${monthLabel(startMonth, monthlyClosingDate)} 〜 ${monthLabel(endMonth, monthlyClosingDate)} を出力します。`
                  : '選んだ月の開始から終了までを出力します。'}
              </p>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-auto"
                disabled={mastersLoading}
              />
              <span>〜</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-auto"
                disabled={mastersLoading}
              />
            </div>
          )}
        </div>

        {/* 週始まり（月次は締日で区切るため表示しない） */}
        {!isMonthly && (
          <div className="space-y-2">
            <Label>週始まり</Label>
            <RadioGroup
              value={weekStartDay}
              onValueChange={(v) => setWeekStartDay(v as WeekStartDay)}
              className="flex gap-6"
              disabled={mastersLoading}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="monday" id="week-monday" />
                <Label htmlFor="week-monday" className="font-normal">
                  月曜始まり
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="sunday" id="week-sunday" />
                <Label htmlFor="week-sunday" className="font-normal">
                  日曜始まり
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* 集計単位（排他選択。選択したカードの直下にタイプ選択UIが続く） */}
        <div className="space-y-2">
          <Label id="aggregation-mode-label">集計単位を選択</Label>
          <RadioGroup
            aria-labelledby="aggregation-mode-label"
            value={aggregationMode}
            onValueChange={(v) => handleAggregationModeChange(v as AggregationMode)}
            className="grid gap-3 sm:grid-cols-2"
          >
            {AGGREGATION_MODE_OPTIONS.map((option) => {
              const selected = aggregationMode === option.value;
              return (
                <Label
                  key={option.value}
                  htmlFor={option.id}
                  className={cn(
                    'flex cursor-pointer flex-col gap-2 rounded-lg border p-4 font-normal transition-colors',
                    // ラジオ本体は内側のボタンなので、フォーカスリングはカード全体で受ける
                    'has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-ring has-[button:focus-visible]:ring-offset-2',
                    selected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-input hover:bg-accent/50',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value={option.value} id={option.id} aria-label={option.title} />
                    <span className="text-sm font-medium">{option.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                  <div className="space-y-1">
                    <div className="text-[11px] text-gray-500">出力列イメージ</div>
                    <div className="flex flex-wrap items-center gap-1">
                      {previewColumnsOf(option.value).map((col) => (
                        <PreviewColumn
                          key={col.key}
                          label={col.label}
                          placeholder={col.placeholder}
                        />
                      ))}
                    </div>
                  </div>
                </Label>
              );
            })}
          </RadioGroup>
        </div>

        {/* 商品グループ（タイプ選択。階層は既定で最下層、折りたたみ内で調整可能） */}
        <div className="space-y-2">
          <Label>
            {isCross
              ? `掛け合わせる商品グループタイプ（最大${MAX_CROSS_TYPES}件。選んだ順が列順になります）`
              : '集計対象の商品グループ（出力するタイプを選択。既定で各タイプの最下層まで出力します）'}
          </Label>
          {mastersLoading ? (
            <div className="flex items-center rounded-md border p-3 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              読み込み中...
            </div>
          ) : productGroupTypes.length === 0 ? (
            <div className="rounded-md border p-3 text-sm text-gray-500">
              商品グループタイプがありません
            </div>
          ) : (
            <div className="rounded-md border p-3">
              {/* 掛け合わせの列順（選んだ順＝左からの列順。ここで入れ替えられる）。
                  ラベルの「選んだ順が列順になります」だけでは実際の並びが分からないため、
                  タイプ選択より前に現在の並びを出す（未選択でも枠を出して場所を示す） */}
              {isCross && (
                <div className="mb-3 rounded-md bg-gray-50 p-2">
                  <div className="mb-1 text-xs text-gray-600">
                    現在の列順（CSVではこの順で左から並びます）
                  </div>
                  {selections.length === 0 ? (
                    <div className="text-xs text-gray-400">
                      下でタイプを選ぶと、ここに列順が表示されます
                    </div>
                  ) : (
                    <ol className="flex flex-wrap items-center gap-1">
                      {selections.map((selection, index) => {
                        const name = selectedTypeNames[index];
                        return (
                          <li key={selection.type_id} className="flex items-center gap-1">
                            {/* 順序を「→」で明示する（番号だけだと並びとして読み取りにくい） */}
                            {index > 0 && <span className="text-gray-400">→</span>}
                            <span className="flex items-center gap-0.5 rounded border bg-white px-1.5 py-0.5 text-xs">
                              <span className="text-gray-400">{index + 1}.</span>
                              <span>{name}</span>
                              <button
                                type="button"
                                aria-label={`${name}を左へ移動`}
                                disabled={index === 0}
                                onClick={() => moveCrossSelection(index, -1)}
                                className="px-0.5 text-gray-500 disabled:opacity-30"
                              >
                                <ChevronLeft className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                aria-label={`${name}を右へ移動`}
                                disabled={index === selections.length - 1}
                                onClick={() => moveCrossSelection(index, 1)}
                                className="px-0.5 text-gray-500 disabled:opacity-30"
                              >
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {productGroupTypes.map((type) => {
                  const order = selectedIndexById.get(type.id);
                  const checked = order !== undefined;
                  const disabled = !checked && selections.length >= maxSelectedTypes;
                  return (
                    <Chip
                      key={type.id}
                      // 掛け合わせでは選んだ順が列順になるため、序数をラベルに出す
                      label={isCross && checked ? `${order + 1}. ${type.name}` : type.name}
                      color={checked ? 'primary' : 'default'}
                      variant={checked ? 'filled' : 'outlined'}
                      onClick={() => toggleType(type.id)}
                      disabled={disabled}
                      sx={{ height: 28 }}
                    />
                  );
                })}
              </Box>
              <div className="mt-1 text-xs text-gray-500">
                選択数: {selections.length} / {maxSelectedTypes}
                {/* 上限到達は継続する状態なので、消えるトーストではなく常設のテキストで理由を示す
                    （MUI Chip は disabled だと onClick が発火しないためトーストは出せない） */}
                {selections.length >= maxSelectedTypes && (
                  <span className="ml-2 text-amber-600">
                    上限に達しています。別のタイプを選ぶには、選択済みのタイプを外してください
                  </span>
                )}
              </div>

              {/* 掛け合わせの組み合わせ数の見積もり。行数が膨らむ前に気づけるよう実行前に出す */}
              {isCross && selections.length > 0 && (
                <div className="mt-2 text-xs">
                  {levelCountsLoading ? (
                    <span className="flex items-center text-gray-500">
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      組み合わせ数を見積もり中...
                    </span>
                  ) : crossEstimate === null ? null : crossEstimate.total >=
                    CROSS_COMBINATION_WARN_THRESHOLD ? (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800">
                      <div className="font-medium">
                        {`選択中の組み合わせは最大 約${crossEstimate.total.toLocaleString()} 通りです`}
                      </div>
                      <div className="mt-1">
                        {'これに週数と店舗数が掛かるため、Excel で開けない規模の行数になる可能性があります。' +
                          '下の「集計階層を調整する」で階層を浅くすると大きく減ります' +
                          (crossEstimate.atDepth1 < crossEstimate.total
                            ? `（全て1階層にすると 約${crossEstimate.atDepth1.toLocaleString()} 通り）`
                            : '') +
                          '。'}
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {crossEstimate.types.map((t) => (
                          <li key={t.name}>
                            {`・${t.name}: ${t.depth === null ? '最下層' : `${t.depth}階層`} ${t.current.toLocaleString()} グループ` +
                              (t.depth !== null && t.lowest > t.current
                                ? `（最下層なら ${t.lowest.toLocaleString()}）`
                                : '')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <span className="text-gray-500">
                      {`選択中の組み合わせは最大 約${crossEstimate.total.toLocaleString()} 通りです`}
                    </span>
                  )}
                </div>
              )}

              {/* 集計階層の調整（既定は最下層のため通常は開かない） */}
              {selections.length > 0 && (
                <Collapsible
                  open={depthPanelOpen}
                  onOpenChange={setDepthPanelOpen}
                  className="mt-3 border-t pt-3"
                >
                  <CollapsibleTrigger className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                    {depthPanelOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    集計階層を調整する（既定: {isCross ? `${CROSS_DEFAULT_DEPTH}階層` : '最下層'}）
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-3">
                    <p className="text-xs text-gray-500">
                      {isCross
                        ? '掛け合わせは選択したタイプの組み合わせ分だけ行が増えます（各タイプの最下層グループ数の積）。最下層のまま3タイプを掛け合わせると数千万行規模になることがあるため、浅い階層の指定を推奨します。'
                        : '階層の深い商品グループタイプを長期間で最下層まで出力すると、数千万行規模になり Excel で開けなくなることがあります。その場合はここで階層を浅く指定してください。'}
                    </p>
                    {selections.map((selection, index) => {
                      const typeId = selection.type_id;
                      const type = productGroupTypes.find((t) => t.id === typeId);
                      const currentDepth = selection.depth ?? null;
                      return (
                        <div key={typeId} className="border-l-2 border-gray-200 pl-3">
                          <div className="mb-1 text-sm">
                            {isCross ? `${index + 1}. ` : ''}
                            {type?.name ?? typeId} の集計階層
                          </div>
                          <RadioGroup
                            value={String(currentDepth)}
                            onValueChange={(v) =>
                              changeDepth(typeId, v === 'null' ? null : Number(v))
                            }
                            className="flex flex-wrap gap-4"
                          >
                            {DEPTH_OPTIONS.map((depth) => {
                              const id = `depth-${typeId}-${depth ?? 'lowest'}`;
                              return (
                                <div key={id} className="flex items-center gap-1.5">
                                  <RadioGroupItem value={String(depth)} id={id} />
                                  <Label htmlFor={id} className="font-normal">
                                    {depthLabel(depth)}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </div>

        {/* 店舗軸の出力粒度（FKV3-12856）。直下のツリーの意味が変わるため、その直前に置く（FKV3-13085） */}
        <StoreGranularitySelect
          value={storeAggregationMode}
          onValueChange={setStoreAggregationMode}
          idPrefix="weekly-store-granularity"
          disabled={mastersLoading}
        />

        {/* 集計対象倉庫（デフォルト全チェック。未選択なら全倉庫） */}
        <div className="space-y-2">
          <Label>
            {isByGroup
              ? '集計する店舗グループ（選んだグループごとに1行を出力）'
              : '集計対象の倉庫（既定は全倉庫。検索・一括選択が可能）'}
          </Label>
          <p className="text-sm text-muted-foreground">
            {isByGroup ? (
              <>
                選んだグループが<strong>そのまま出力の1行</strong>
                になり、その配下が対象範囲になります。親と子は同時に選べません。
              </>
            ) : (
              <>
                ここで絞るのは店舗明細CSVの<strong>対象範囲</strong>です。
              </>
            )}
            物流倉庫・発注残（中央倉庫）の在庫は別CSVに、この選択に関わらず常に全量出力します。
          </p>
          {isByGroup ? (
            <StoreGroupExclusiveSelect
              nodes={warehouseNodes}
              selectedIds={storeGroupNodeIds}
              onChange={setStoreGroupNodeIds}
              idPrefix="target-sg"
              loading={mastersLoading}
            />
          ) : (
            <div className="space-y-2">
              <WarehouseQuickSelect
                accountCode={account.code}
                selectedIds={selectedTargetWarehouseIds}
                onChange={setSelectedTargetWarehouseIds}
                idPrefix="target-wh"
                disabled={mastersLoading}
              />
              <WarehouseMultiSelect
                nodes={warehouseNodes}
                selectedIds={selectedTargetWarehouseIds}
                onChange={setSelectedTargetWarehouseIds}
                idPrefix="target-wh"
                loading={mastersLoading}
              />
            </div>
          )}
        </div>

        {/* 非稼働判定日数 */}
        <div className="space-y-2">
          <Label htmlFor="inactive-days">非稼働判定日数（既定30日）</Label>
          <Input
            id="inactive-days"
            type="number"
            min={INACTIVE_DAYS_MIN}
            max={INACTIVE_DAYS_MAX}
            value={inactiveDays}
            onChange={(e) => setInactiveDays(Number(e.target.value))}
            className="w-32"
            disabled={mastersLoading}
          />
          <p className="text-sm text-gray-500">
            各週末から直近この日数に販売がない在庫を非稼働在庫とみなします
          </p>
        </div>

        {/* 前々年比較ファイルの追加出力（既定OFF。FKV3-12587） */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-two-years-ago"
              checked={includeTwoYearsAgo}
              onCheckedChange={(checked) => setIncludeTwoYearsAgo(checked === true)}
              disabled={mastersLoading}
            />
            <Label htmlFor="include-two-years-ago" className="font-normal">
              前々年実績も出力する（本年×前々年のCSVを追加で出力）
            </Label>
          </div>
          <p className="text-sm text-gray-500">
            ONにすると「本年×前年」に加えて「本年×前々年」のCSVも出力するため、集計時間がおよそ2倍になります
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            抽出を実行
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
